<template>
  <div v-if="(isCreateFormVisible || isListFormVisible) && ((isModalMode && isModalOpened) || !isModalMode)">
    <aw-tab-version2
      v-if="(isModalMode && !showMapForm) || !isModalMode"
      ref="tabContainer"
      tab-type="container"
      :header-items="tabsComputed"
      @mounted="handleTabMounted"
    >
      <template #content>
        <aw-tab-content-version2
          v-for="tab in tabsComputed"
          :key="tab.id"
          :tab-index="tab.tabItemId"
          :keep-dom="false"
        >
          <aw-checkout-map-block
            ref="mapBlock"
            :key="`map-block-${tab.id}`"
            :tab-data="tab"
            :heading-tag="headingTag"
            @select-pickup-point="handleSelectPickupPoint"
            @get-available-points-success="emit('get-available-points-success')"
          />
        </aw-tab-content-version2>
      </template>
    </aw-tab-version2>

    <aw-checkout-delivery-pickup-or-locker-data-form
      v-if="showMapForm && selectedDeliveryMethod"
      ref="deliveryPickupOrLockerForm"
      :is-modal-mode="isModalMode"
      :class="$style.formContainer"
      :is-guest-checkout="isGuestCheckout"
      :selected-delivery-method="selectedDeliveryMethod"
      :selected-point="selectedPoint"
      :is-dm-modal-mode="isDmModalMode"
      @pickup-point-data-saved="onPickupPointDataSaved"
      @back-to-map="handleBackToMap"
      @validation-status-changed="emit('validation-status-changed', $event)"
    />
  </div>
</template>
<script setup lang="ts">
  import { defineAsyncComponent, computed, ref, watch, useTemplateRef } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import {
    DM_DELIVERY_PICKUP,
    DM_DELIVERY_LOCKER,
    DM_DELIVERY_COOLOMAT,
  } from '~~/common/config/DeliveryMethod';
  import { useUserStore } from '~~/common/stores/user';
  import AwTabContentVersion2 from '~~/common/components/Common/AwTabContentVersion2.vue';
  import AwCheckoutMapBlock from '~~/shop/app/components/Page/Checkout/AwCheckoutMapBlock.vue';
  import AwCheckoutDeliveryPickupOrLockerDataForm from '~~/shop/app/components/Page/Checkout/DeliveryData/AwCheckoutDeliveryPickupOrLockerDataForm.vue';
  import { useTabStore } from '~~/common/stores/tab.js';
  import { injectCheckout } from '~~/shop/app/config/checkoutInjection';
  import type { CurrentCheckout, UpdateDeliveryData, PickupPoint } from '~~/shop/app/config/checkoutInjection';
  import type { CheckoutData } from '~~/common/stores/checkout';
  import type { MappedDeliveryMethod, ChildDeliveryMethod } from '~~/shop/app/config/DeliveryMethodConfig';
    
  /** One delivery-method tab of the pickup point map. */
  type PickupTab = {
    label: string;
    icon?: string;
    id: string;
    data: Record<string, ChildDeliveryMethod>;
    tabItemId?: string;
  };


  const AwTabVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwTabVersion2.vue'));

  const {
    $awt,
  } = useNuxtApp();

  const currentCheckout = injectCheckout<CurrentCheckout>('currentCheckout');
  const tabStore = useTabStore();

  const props = withDefaults(defineProps<{
    selectedDeliveryMethod?: MappedDeliveryMethod | null;
    deliveryData?: CheckoutData['delivery'] | null;
    isModalMode: boolean;
    isDmModalMode?: boolean;
    isModalOpened: boolean;
    isCreateFormVisible: boolean;
    isListFormVisible: boolean;
    headingTag: string;
    /** Delivery-method constant of the tab to activate, passed by both callers. */
    currentDeliveryMethodTab?: string | null;
  }>(), {
    selectedDeliveryMethod: null,
    deliveryData: null,
    isDmModalMode: false,
    currentDeliveryMethodTab: null,
  });

  const isGuestCheckout = computed(() => !useUserStore().isEcomUser);

  const showMapForm = ref(false);
  const deliveryPickupOrLockerFormRef = useTemplateRef('deliveryPickupOrLockerForm');
  const mapBlockRef = useTemplateRef('mapBlock');
  const tabContainerRef = useTemplateRef<{ uuid: string }>('tabContainer');

  const updateDeliveryData = injectCheckout<UpdateDeliveryData>('updateDeliveryData');

  const emit = defineEmits(['update:is-create-form-visible', 'update:is-list-form-visible', 'close-modal', 'select-pickup-point', 'get-available-points-success', 'validation-status-changed']);
  {
    const icfv = !props.deliveryData?.pickupPointData && props.selectedDeliveryMethod?.id;
    emit('update:is-create-form-visible', !!icfv);

    showMapForm.value = !props.isModalMode;
  }

  const selectedPoint = ref<PickupPoint | null>(null);

  const tabsComputed = computed(() => {
    const tabs: PickupTab[] = [];

    for (const dm of [DM_DELIVERY_COOLOMAT, DM_DELIVERY_PICKUP, DM_DELIVERY_LOCKER]) {
      const childrenGroup = props.selectedDeliveryMethod?.childrenGroups?.[dm];
      if (childrenGroup) {
        const {
          available,
          token,
          children,
        } = childrenGroup;
        if (available.length) {
          const tabData = (Object.entries(children)
            .filter(([, item]) => item.id)
            .reduce<Record<string, ChildDeliveryMethod>>((acc, [key, item]) => {
              acc[key] = item;
              return acc;
            }, {})
          );
          const isPromotional = Object.values(tabData).some(
            (child) => child.promotional || child.nonFoodCourier?.promotional,
          );
          tabs.push({
            label: $awt(token),
            icon: isPromotional ? 'pig-16' : undefined,
            id: dm,
            data: tabData,
          });
        }
      }
    }

    return tabs.map((tab, index) => ({
      tabItemId: index,
      ...tab,
    }));
  });

  watch(() => props.selectedDeliveryMethod?.id, (newValue) => {
    if (newValue && !props.deliveryData?.pickupPointData && !props.isModalMode) {
      emit('update:is-create-form-visible', true);
    }
  });

  function onEditClicked () {
    updateDeliveryData(null);
    emit('update:is-create-form-visible', true);
  }

  function onPickupPointDataSaved () {
    emit('update:is-create-form-visible', false);
    emit('update:is-list-form-visible', false);
    updateDeliveryData(currentCheckout.value.delivery);
    emit('close-modal', { afterSave: true });
  }

  function handleSelectPickupPoint (point: PickupPoint) {
    selectedPoint.value = point;
    if (props.isModalMode) {
      showMapForm.value = true;
    }
    if (props.isDmModalMode) {
      emit('select-pickup-point', point);
    }
  }

  function handleBackToMap () {
    showMapForm.value = false;
  }

  function handleTabMounted () {
    const tabContainer = tabContainerRef.value;
    if (props.currentDeliveryMethodTab && tabContainer) {
      //change tab if necessary
      const activeTabIndex = tabStore.tabs[tabContainer.uuid];
      const currentDeliveryMethodTabItemId = tabsComputed.value.find(tab => tab?.id === props.currentDeliveryMethodTab)?.tabItemId;

      if (currentDeliveryMethodTabItemId && currentDeliveryMethodTabItemId !== activeTabIndex) {
        tabStore.setTab({
          uuid: tabContainer.uuid,
          index: currentDeliveryMethodTabItemId,
        });
      }
    }
  }

  defineExpose({
    onEditClicked,
    showMapForm,
    deliveryPickupOrLockerFormRef,
    mapBlockRef,
  });
</script>

<style module lang="scss" rel="stylesheet/scss">

.content {
  margin-bottom: 20px;

  @include tablet(min) {
    margin-bottom: 40px;
  }
}

[data-tab-items] {
  --awTabItemPadding: 12px 16px;
  justify-content: center;
}

.profilePageContainer {
  margin-top: 0;

  @include mobile(max) {
    padding-top: 70px !important;
  }
}

.formContainer {
  @include tablet(min) {
    margin-top: 40px;
  }
}
</style>
